import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import FbButton from 'components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { Tabs } from 'components/FbUI/Tabs'
import { useMemo, useState, useCallback } from 'react'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { CSVRow, InternalColumns } from '../importer-types'
import { useImporterViewModel, getColumnOption } from '../ImporterViewModel'
import { OppTypeBadge, getOppTypeLabel } from './OppTypeBadge'
import { CompanyActionCell } from './CompanyActionCell'
import { setAsExcludedBulk } from '../importDataTransforms'
import { useContainerHeight } from './useContainerHeight'

type FinishTab = 'matched' | 'unmatched'

export function ImportWizardFinishMatchingStep({
  show,
  data,
  skipMatching = false,
}: {
  show: boolean
  data: CSVRow[]
  skipMatching?: boolean
}) {
  const viewModel = useImporterViewModel()
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const [tab, setTab] = useState<FinishTab>('unmatched')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [tableContainerRef, tableContainerHeight] = useContainerHeight()

  // Identify company_match columns from headers
  const companyMatchHeaders = useMemo(() => {
    return (
      viewModel.importData?.headers.filter(
        (h: { original: string; mapped: string }) =>
          getColumnOption(h.mapped)?.type === 'company_match'
      ) ?? []
    )
  }, [viewModel.importData?.headers])

  // Split data into matched and unmatched
  const { matchedData, unmatchedData } = useMemo(() => {
    const matched: CSVRow[] = []
    const unmatched: CSVRow[] = []
    data.forEach((row) => {
      const chain = row[InternalColumns.MATCHED_CHAIN]
      const door = row[InternalColumns.MATCHED_DOOR]
      if (chain || door) {
        matched.push(row)
      } else {
        unmatched.push(row)
      }
    })
    return { matchedData: matched, unmatchedData: unmatched }
  }, [data])

  const currentData = tab === 'matched' ? matchedData : unmatchedData

  // Get selected row hashes for bulk actions
  const selectedHashes = useMemo(() => {
    return Object.entries(rowSelection)
      .filter(([, selected]) => selected)
      .map(([hash]) => hash)
  }, [rowSelection])

  const selectedCount = selectedHashes.length

  // Bulk action handler
  const handleBulkExclude = useCallback(() => {
    if (selectedHashes.length === 0) return
    viewModel.updateImportData((data) =>
      setAsExcludedBulk(data, selectedHashes)
    )
    setRowSelection({})
  }, [selectedHashes, viewModel])

  const columnDefs: ColumnDef<CSVRow>[] = useMemo(() => {
    const columns: ColumnDef<CSVRow>[] = []

    // Input Data column
    columns.push({
      id: 'input_data',
      header: 'Input Data',
      accessorFn: (row) => {
        return companyMatchHeaders
          .map((h: { original: string }) => row[h.original] || '')
          .join(' ')
          .toLowerCase()
      },
      cell: ({ row }) => {
        const items: { label: string; value: string }[] = []

        companyMatchHeaders.forEach(
          (header: { original: string; mapped: string }) => {
            const value = row.original[header.original]
            if (value) {
              items.push({
                label: getColumnOption(header.mapped)?.name || header.mapped,
                value,
              })
            }
          }
        )

        return (
          <div className="space-y-0.5 text-sm">
            {items.map((item, idx) => (
              <div key={idx} className="text-gray-700">
                <span className="font-medium text-gray-500">{item.label}:</span>{' '}
                {item.value}
              </div>
            ))}
          </div>
        )
      },
    })

    // Matched Data column (for matched tab)
    if (tab === 'matched') {
      columns.push({
        id: 'matched_data',
        header: 'Matched Data',
        accessorFn: (row) => {
          const chain = row[InternalColumns.MATCHED_CHAIN]
          const door = row[InternalColumns.MATCHED_DOOR]
          return (door?.name || chain?.name || '').toLowerCase()
        },
        cell: ({ row }) => {
          const chain = row.original[InternalColumns.MATCHED_CHAIN]
          const door = row.original[InternalColumns.MATCHED_DOOR]
          const isMatched = chain || door

          if (!isMatched) {
            return <span className="text-gray-400">-</span>
          }

          const items: {
            label: string
            value: string
            link?: string | null
          }[] = []

          // Build company ID for link
          const companyId = door
            ? `${door.type}${door.item_id}`
            : chain
              ? `${chain.type}${chain.item_id}`
              : null
          const companyUrl = companyId ? getCompanyUrlFromId(companyId) : null

          if (door?.name || chain?.name) {
            items.push({
              label: 'Name',
              value: door?.name || chain?.name || '-',
              link: companyUrl,
            })
          }
          if (door?.domain || chain?.domain) {
            items.push({
              label: 'Domain',
              value: door?.domain || chain?.domain || '-',
            })
          }
          if (door?.address) {
            items.push({ label: 'Address', value: door.address })
          }
          if (chain?.count || door?.chain_size) {
            items.push({
              label: 'Doors',
              value: String(chain?.count || door?.chain_size || '-'),
            })
          }

          return (
            <div className="space-y-0.5 text-sm">
              {items.map((item, idx) => (
                <div key={idx} className="text-gray-700">
                  <span className="font-medium text-gray-500">
                    {item.label}:
                  </span>{' '}
                  {item.link ? (
                    <FbLink to={item.link} target="_blank">
                      {item.value}
                    </FbLink>
                  ) : (
                    item.value
                  )}
                </div>
              ))}
            </div>
          )
        },
      })

      // Company Target Type column (for matched tab)
      columns.push({
        id: 'company_target_type',
        header: 'Company Target Type',
        accessorFn: (row) => {
          const chain = row[InternalColumns.MATCHED_CHAIN]
          const door = row[InternalColumns.MATCHED_DOOR]
          const typeCode = door?.type || chain?.type
          return getOppTypeLabel(typeCode) || ''
        },
        cell: ({ row }) => {
          const chain = row.original[InternalColumns.MATCHED_CHAIN]
          const door = row.original[InternalColumns.MATCHED_DOOR]
          const typeCode = door?.type || chain?.type
          if (!typeCode) return '-'
          return <OppTypeBadge typeCode={typeCode} />
        },
      })
    }

    // Action column for unmatched tab
    if (tab === 'unmatched') {
      columns.push({
        id: 'action',
        header: 'Action',
        cell: ({ row }) => <CompanyActionCell row={row.original} compact />,
        size: 220,
      })
    }

    return columns
  }, [companyMatchHeaders, getCompanyUrlFromId, tab])

  if (!show) {
    return null
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {skipMatching ? (
        // Skip matching mode: Show all companies with per-row action dropdowns
        <>
          <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex-shrink-0">
            <p className="text-sm text-purple-800">
              <strong>Skip Matching:</strong> Set each company&apos;s action
              below. Default is Exclude.
            </p>
          </div>

          {/* Bulk action bar */}
          {selectedCount > 0 && (
            <div className="flex-shrink-0">
              <BulkActionBar
                selectedCount={selectedCount}
                onExclude={handleBulkExclude}
              />
            </div>
          )}

          <div ref={tableContainerRef} className="flex-1 min-h-0">
            <ClientDataTable
              height={tableContainerHeight || 400}
              data={data}
              columns={columnDefs}
              enableSorting
              enableRowSelection
              selectedRows={rowSelection}
              onRowSelectionChange={setRowSelection}
              selectionRowID={InternalColumns.COMPANY_HASH as keyof CSVRow}
            />
          </div>
        </>
      ) : (
        // Normal mode: Show matched/unmatched tabs
        <>
          <div className="flex-shrink-0">
            <Tabs
              tabs={[
                {
                  key: 'unmatched',
                  title: `Unmatched (${unmatchedData.length})`,
                },
                {
                  key: 'matched',
                  title: `Matched (${matchedData.length})`,
                },
              ]}
              selectedTab={tab}
              setSelectedTab={(t) => {
                setTab(t)
                setRowSelection({}) // Clear selection when switching tabs
              }}
            />
          </div>

          {/* Bulk action bar for unmatched tab */}
          {tab === 'unmatched' && selectedCount > 0 && (
            <div className="flex-shrink-0">
              <BulkActionBar
                selectedCount={selectedCount}
                onExclude={handleBulkExclude}
              />
            </div>
          )}

          <div ref={tableContainerRef} className="flex-1 min-h-0">
            <ClientDataTable
              height={tableContainerHeight || 400}
              data={currentData}
              columns={columnDefs}
              enableSorting
              enableRowSelection={tab === 'unmatched'}
              selectedRows={tab === 'unmatched' ? rowSelection : {}}
              onRowSelectionChange={
                tab === 'unmatched' ? setRowSelection : undefined
              }
              selectionRowID={InternalColumns.COMPANY_HASH as keyof CSVRow}
            />
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Bulk action bar for setting selected rows as excluded
 */
function BulkActionBar({
  selectedCount,
  onExclude,
}: {
  selectedCount: number
  onExclude: () => void
}) {
  return (
    <div className="flex items-center gap-3 p-3 mb-2 bg-blue-50 border border-blue-200 rounded-lg">
      <span className="text-sm font-medium text-blue-800">
        {selectedCount} selected
      </span>
      <FbButton variant="secondary" onClick={onExclude}>
        Set Exclude
      </FbButton>
    </div>
  )
}
